"use client";
import { useState, useEffect } from "react";
import { Box, Center, Flex, Text, Button, Group, NumberInput } from "@chakra-ui/react";
import Image from "next/image";
import AmdLogo from "public/pricingPage/amd.webp";
import IntelLogo from "public/pricingPage/intel.png";
import AmdWLogo from "public/pricing/amd-w-logo.png";
import IntelWLogo from "public/pricing/intel-logo-white.png";

const PRICE_UNIT = {
  HOURLY: "hourly",
  MONTHLY: "monthly",
};

const SERVER_TYPE = {
  INTEL: "intel",
  AMD: "amd",
};

export const PricingGroup = ({ frequency, onChange }) => {
  return (
    <Flex direction="column" alignItems="flex-end" mb={4}>
      <Box>
        <Text fontWeight={600} color="gray.600">
          Pricing Mode
        </Text>
        <Group attached>
          <Button
            py={0}
            onClick={() => onChange(PRICE_UNIT.HOURLY)}
            bg={frequency === PRICE_UNIT.HOURLY ? "primary.200" : "transparent"}
            color={frequency === PRICE_UNIT.HOURLY ? "white" : "gray.700"}
            border="1px solid"
            borderRadius={"md"}
            borderColor="gray.400"
            _hover={{ bg: frequency === PRICE_UNIT.HOURLY ? "primary.200" : "gray.100" }}
            _active={{ bg: "primary.200", color: "white" }}
            variant="outline"
          >
            Hourly
          </Button>
          <Button
            py={0}
            onClick={() => onChange(PRICE_UNIT.MONTHLY)}
            bg={frequency === PRICE_UNIT.MONTHLY ? "primary.200" : "transparent"}
            color={frequency === PRICE_UNIT.MONTHLY ? "white" : "gray.700"}
            border="1px solid"
            borderRadius={"md"}
            borderColor="gray.400"
            _hover={{ bg: frequency === PRICE_UNIT.MONTHLY ? "primary.200" : "gray.100" }}
            _active={{ bg: "primary.200", color: "white" }}
            variant="outline"
          >
            Monthly
          </Button>
        </Group>
      </Box>
    </Flex>
  );
};

export const ServerGroup = ({ server, onChange }) => {
  return (
    <Center mb={{ base: 4, sm: 8, lg: 4, "2xl": 1 }}>
      <Group attached>
        <Button
          py={5}
          px={{ base: 2, sm: 6, md: 8, lg: 12 }}
          onClick={() => onChange(SERVER_TYPE.INTEL)}
          bg={server === SERVER_TYPE.INTEL ? "primary.200" : "transparent"}
          color={server === SERVER_TYPE.INTEL ? "white" : "gray.700"}
          border="1px solid"
          borderColor="gray.400"
          fontWeight={550}
          fontSize={"md"}
          _hover={{ bg: server === SERVER_TYPE.INTEL ? "primary.200" : "gray.200" }}
          _active={{ bg: "primary.200", color: "white" }}
          variant="outline"
          borderRadius="lg"
        >
          <Box
            h="2rem"
            position="relative"
            w="2rem"
            display="flex"
            alignItems="center"
            mr="10px"
          >
            <Image
              src={server === SERVER_TYPE.INTEL ? IntelWLogo : IntelLogo}
              alt="image"
              sizes="38px"
              style={{ objectFit: "contain" }}
            />
          </Box>
          Intel Resources
        </Button>
        <Button
          py={5}
          px={{ base: 2, sm: 6, md: 8, lg: 12 }}
          onClick={() => onChange(SERVER_TYPE.AMD)}
          bg={server === SERVER_TYPE.AMD ? "primary.200" : "transparent"}
          color={server === SERVER_TYPE.AMD ? "white" : "gray.700"}
          border="1px solid"
          borderColor="gray.400"
          fontWeight={550}
          fontSize={"md"}
          _hover={{ bg: server === SERVER_TYPE.AMD ? "primary.200" : "gray.200" }}
          _active={{ bg: "primary.200", color: "white" }}
          variant="outline"
          borderRadius="lg"
        >
          <Box
            h="2rem"
            position="relative"
            w="2rem"
            display="flex"
            alignItems="center"
            mr="10px"
          >
            <Image
              src={server === SERVER_TYPE.AMD ? AmdWLogo : AmdLogo}
              alt="image"
              sizes="38px"
              style={{ objectFit: "contain" }}
            />
          </Box>
          Amd Resources
        </Button>
      </Group>
    </Center>
  );
};

export const NodeCountInput = ({ nodeCount, onChange }) => {
  const [inputValue, setInputValue] = useState(nodeCount.toString());
  const [error, setError] = useState("");

  useEffect(() => {
    setInputValue(nodeCount.toString());
  }, [nodeCount]);

  const handleInputChange = (details) => {
    const valueString = details.value;
    setInputValue(valueString);

    if (valueString === "") {
      setError("Value required.");
      return;
    }

    const value = parseInt(valueString, 10);

    if (isNaN(value)) {
      setError("Invalid number.");
      return;
    }

    if (value < 3) {
      setError("Minimum nodes can be 3.");
      return;
    }

    if (value > 100) {
      setError("Maximum nodes can be 100.");
      return;
    }

    setError("");
    onChange(value);
  };

  return (
    <Flex direction="column" align="center" gap={2} mb={4}>
      <Box>
        <Text fontWeight={600} color="gray.600">
          Enter number of Nodes
        </Text>
        <NumberInput.Root
          size="md"
          value={inputValue}
          onValueChange={handleInputChange}
          min={3}
          max={100}
          maxW="120px"
        >
          <NumberInput.Input
            border="1px solid"
            borderColor="gray.400"
            borderRadius="20px"
          />
        </NumberInput.Root>

        {error && (
          <Text fontSize="sm" color="red.500" mt={0}>
            {error}
          </Text>
        )}
      </Box>
    </Flex>
  );
};
